import { AppConstant, KeyConstant, SystemConstant } from "const";
import { call, put, select } from "redux-saga/effects";
import { convertJSONObject, convertString2JSON, isE2E, isJSONString, StorageUtil, toCamel, uuid } from "utils";
import { getInteractor } from "services/local.service";
import store, {
  CallingActions,
  ConversationActions,
  ConversationSelectors,
  GroupInfoActions,
  SystemActions,
} from "redux-store";
import { updateThread } from "./thread.saga";
import { getSendType } from "utils/view.utils";
import { isEncryptionType } from "./saga.helper";
import { Mutex } from "async-mutex";
import { saveKeysByAccountIds } from "./account-key.saga";
import { synchDeviceInGroup } from "pubsub/services/device.service";
import { createGroupService } from "pubsub/services/conversation.service";
import { getCommonLang } from "utils/lang.utils";

const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;
const getProtocol = window.bglobalSignal.libs.util.getProtocol;
const getProtocolGroup = window.bglobalSignal.libs.util.getProtocolGroup;

const mutexes = {};

// Save messages locally and storing encrypted messages in queue table for pending send to the server
export function* saveMsgInQueue(action) {
  const { groupId, sendType, roomId, content, branchId, callStatus } = action.data;
  const mutex = mutexes[groupId] || new Mutex();
  mutexes[groupId] = mutex;

  const release = yield mutex.acquire();
  try {
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();

    const branchServerId = branchId || StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);

    // Message with sendType !== [deleteMessage, deleteMessageOnlyMe] must have content
    const isValidData = content && branchServerId && groupId;
    const isCallingType = SystemConstant.ARR_CALLING_TYPES.includes(sendType);
    const isValidCalling =
      isCallingType && roomId && Object.values(SystemConstant.MESSAGE_CALL_STATUS).includes(callStatus);
    if (!isValidData || (isCallingType && !isValidCalling)) {
      return;
    }

    let groupDetail = yield getInteractor(prefixKey).LocalGroupService.get(groupId);

    if (groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal && groupDetail.draftGroupMembers) {
      const groupMembers = groupDetail.draftGroupMembers;
      const createGroupData = {
        encryptionF: SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION,
        groupId: groupId,
        name: groupDetail.name,
        groupType: SystemConstant.GROUP_CHAT_TYPE.personal,
        members: groupMembers,
        privateF: 0,
      };

      const remoteGroup = yield createGroupService(prefixKey, createGroupData);
      if (false === Boolean(remoteGroup && remoteGroup.id)) {
        yield put(
          SystemActions.systemSet({
            toast: {
              content: getCommonLang("TXT_SERVER_ERROR"),
              severity: "error",
              modified: Date.now(),
            },
          }),
        );
        return;
      } else {
        groupDetail = yield getInteractor(prefixKey).LocalGroupService.get(groupId);
      }
    }

    const isValidGroup = Boolean(
      groupDetail && groupDetail.id && Array.isArray(groupDetail.groupMembers) && groupDetail.groupMembers.length > 0,
    );
    if (false === isValidGroup) return;

    // Query database by group id for members account ids
    const isPersonal = groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal;
    const memberIdArray = groupDetail.groupMembers.map(member => member.id) || [];
    const newSendType = getSendType(sendType, content, isPersonal);

    // Synch device list by  members account ids
    yield synchDeviceInGroup(prefixKey, groupDetail.id);

    // Synch key by members account ids
    yield call(saveKeysByAccountIds, prefixKey, memberIdArray);
    // Handle message and save it to local
    yield call(isE2E(sendType, groupDetail.groupType) ? sendE2EMessage : sendE2EEMessage, prefixKey, {
      ...action.data,
      groupDetail: groupDetail,
      sendType: newSendType,
      memberIdArray: memberIdArray,
    });
  } catch (e) {
    console.error(e);
  } finally {
    release();
  }
}

/**
 *
 * @param {string} prefixKey
 * @param data
 * @returns {Generator<Promise<*|boolean>|*|boolean|null|Promise<string|null>|Promise<Awaited<unknown>[]>|Generator<*, void, *>|Promise<string>|Promise<string|null|undefined>|Promise<*|null>|Promise<*|null|undefined>|Promise<{prev_number: number, index_number: number, session_data: string | string, plain_text: string, file_id: string, success_number: number, message_key: string | string, error_number: number, waiting_number: number, key_exchange_algorithm: string}>, void, string&*[]>}
 */
function* sendE2EEMessage(prefixKey, data) {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
  const {
    groupDetail,
    parentId,
    content,
    sendType,
    branchId,
    threadId,
    mentionIdsArr,
    memberIdArray,
    callStatus,
    roomId,
    removingId,
    currentMessage,
    option,
    forward,
  } = data;
  const messageOption = isJSONString(option || currentMessage?.options)
    ? convertString2JSON(option || currentMessage?.options, {})
    : option || currentMessage?.options || {};

  const deviceList = yield getInteractor(prefixKey).LocalDeviceService.find({
    account_id: memberIdArray,
    state: SystemConstant.STATE.active,
  });

  const hasEncryption =
    deviceList.findIndex(item => isEncryptionType(item, sendType) !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION) >=
    0;

  if (SystemConstant.NOT_ENCRYPTION_SEND_TYPE.includes(sendType)) {
    messageOption.encryption_f = SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION;
  } else {
    messageOption.encryption_f = hasEncryption
      ? SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION
      : SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION;
  }

  messageOption.original_send_type = sendType;
  messageOption.target_decrypt_send_type = SystemConstant.SEND_TYPE.groupMessage;
  const deviceProtocols = new Set();
  yield Promise.all(
    deviceList.map(async device => {
      const encryptVersion = await getProtocol(prefixKey, device);
      const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
      deviceProtocols.add(encryptTypeProtocol);
    }),
  );

  const encryptTypeProtocol = getProtocolGroup(prefixKey, Array.from(deviceProtocols));

  let values = yield Promise.all(
    deviceList.map(async device => {
      const checkShared = await getInteractor(prefixKey).LocalCryptoService.checkMarkSenderKeyShared(
        device.account_id,
        device.id,
        groupDetail.id,
        encryptTypeProtocol,
      );
      if (checkShared || device.id === deviceId) {
        return null;
      }

      return device;
    }),
  );

  values = values.filter(device => device !== null && device.account_id && device.id);

  let messageList = [];
  const localId = uuid();

  let distributionKey = "";
  if (values.length > 0 && hasEncryption) {
    distributionKey = yield getInteractor(prefixKey).LocalCryptoService.generateDistributionKey(
      accountId,
      deviceId,
      groupDetail.id,
      encryptTypeProtocol,
    );
    messageList = yield Promise.all(
      values.map(async device => {
        const cipherObject = await getInteractor(prefixKey).LocalCryptoService.encryptE2EMessage(
          device.account_id,
          device.id,
          groupDetail.id,
          distributionKey,
          encryptTypeProtocol,
        );
        if (!cipherObject?.cipherText) return null;
        const sessionEntity = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
          device.account_id,
          device.id,
          groupDetail.id,
          encryptTypeProtocol,
        );
        const mesId = deviceId === device.id ? localId : uuid();
        const mes = {
          messageId: mesId,
          sendToDeviceId: device.id,
          sendToAccountId: device.account_id,
          content: cipherObject.cipherText,
          options: JSON.stringify({
            ...messageOption,
            encrypt_version: encryptTypeProtocol,
            index_send_number: cipherObject.indexSendNumber,
            prev_send_number: sessionEntity.prev_index,
            original_send_type: SystemConstant.SEND_TYPE.senderKey,
            target_decrypt_send_type: SystemConstant.SEND_TYPE.senderKey,
            disappearing_f: false,
          }),
          status: SystemConstant.MESSAGE_STATUS.send,
          sendType: SystemConstant.SEND_TYPE.senderKey,
          mentions: "[]",
          callStatus: callStatus,
          roomId: roomId,
          threadId: threadId,
          removingId: removingId,
        };
        let updateValues = {
          prev_index: cipherObject.indexSendNumber,
        };
        await getInteractor(prefixKey).LocalCryptoService.updateSession(
          updateValues,
          device.account_id,
          device.id,
          groupDetail.id,
          encryptTypeProtocol,
        );
        return mes;
      }),
    );
  }

  const messageDistribution = messageList.filter(message => message !== null);
  if (messageDistribution.length > 0) {
    const marks = [];
    const sourceId = uuid();
    const myMessage = {
      account_id: accountId,
      branch_id: branchId,
      content: distributionKey,
      created: Date.now(),
      device_id: deviceId,
      group_id: groupDetail.id,
      id: uuid(),
      mentions: "",
      modified: 0,
      options: JSON.stringify({
        original_send_type: SystemConstant.SEND_TYPE.senderKey,
        target_decrypt_send_type: SystemConstant.SEND_TYPE.senderKey,
        encrypt_version: encryptTypeProtocol,
        disappearing_f: false,
      }),
      parent_id: null,
      send_type: SystemConstant.SEND_TYPE.senderKey,
      sender_device_id: deviceId,
      sender_id: accountId,
      source_id: sourceId,
      state: 1,
      status: SystemConstant.MESSAGE_STATUS.read,
      thread_id: null,
      room_id: roomId,
    };
    yield getInteractor(prefixKey).LocalMessageService.save([myMessage]);

    messageDistribution.forEach(async mesItem => {
      marks.push({
        group_id: groupDetail.id,
        account_id: mesItem.sendToAccountId,
        device_id: mesItem.sendToDeviceId,
        encryption_type_protocol: encryptTypeProtocol,
      });
      let messageDistributionOptions = isJSONString(mesItem.options)
        ? convertString2JSON(mesItem.options, {})
        : mesItem.options;
      const messageEncryptMetaData = await getInteractor(prefixKey).LocalCryptoService.createMessageEncryptionMetaData(
        mesItem.sendToAccountId,
        mesItem.sendToDeviceId,
        content,
        sendType,
        groupDetail.id,
        messageDistributionOptions.index_send_number,
        true,
        encryptTypeProtocol,
      );
      let sessionMessageKey = "";
      const messageEncrypt = {
        id: mesItem.messageId,
        source_id: sourceId,
        send_to_account_id: mesItem.sendToAccountId,
        content: mesItem.content,
        group_id: groupDetail.id,
        message_key: sessionMessageKey,
        session_data: messageEncryptMetaData.session_data,
        file_id: messageEncryptMetaData.file_id,
        key_exchange_algorithm: messageEncryptMetaData.key_exchange_algorithm,
        plain_text: messageEncryptMetaData.plain_text,
        send_to_device_id: mesItem.sendToDeviceId,
        options: mesItem.options,
        created: Date.now(),
        modified: Date.now(),
        index_number: messageEncryptMetaData.index_number,
        waiting_number: messageEncryptMetaData.waiting_number,
        error_number: messageEncryptMetaData.error_number,
        success_number: messageEncryptMetaData.success_number,
        pre_index: messageEncryptMetaData.prev_number,
        session_type: SystemConstant.SESSION_TYPE.session,
        send_type: mesItem.sendType,
        group_type: groupDetail.groupType,
        sender_id: accountId,
        sender_device_id: deviceId,
      };
      await getInteractor(prefixKey).LocalMessageEncryptService.save([messageEncrypt]);
    });

    const newMessage = {
      marks: marks,
      isSendingKey: true,
      groupId: groupDetail.id,
      sourceId: sourceId,
      groupType: groupDetail.groupType,
      sendType: SystemConstant.SEND_TYPE.senderKey,
      created: Date.now(),
      messages: messageDistribution,
      branchId: branchId,
      roomId: roomId,
      threadId: threadId,
      removingId: removingId,
      options: JSON.stringify({ ...messageOption, encrypt_version: encryptTypeProtocol }),
      messageId: localId,
    };
    if (StorageUtil.getCommonKey(KeyConstant.KEY_IGNORE_FIRST_SENDER) === true) {
      StorageUtil.setCommonKey(KeyConstant.KEY_IGNORE_FIRST_SENDER, false);
    } else {
      const apiCallTask = getApiCallTask(sendType);
      yield getInteractor(prefixKey).LocalApiCallService.save([
        {
          id: uuid(),
          task: apiCallTask,
          original_uid: sourceId,
          query: "",
          content: JSON.stringify({ data: newMessage }),
          original_content: JSON.stringify({ data: newMessage }),
          created: new Date().getTime(),
          retry: 0,
          branch_id: branchId,
          group_id: newMessage.groupId,
        },
      ]);
      yield put(SystemActions.executeQueue());
    }
  }

  let messageId = uuid();
  let sourceId = uuid();
  let created = Date.now();
  if (currentMessage) {
    messageId = currentMessage.id;
    sourceId = currentMessage.source_id;
    if (currentMessage.created) created = currentMessage.created;
  }

  const saveMessage = {
    account_id: accountId,
    branch_id: branchId,
    content: content,
    created: created,
    device_id: deviceId,
    group_id: groupDetail.id,
    id: messageId,
    mentions: getMentions(mentionIdsArr),
    modified: 0,
    options: JSON.stringify({ ...messageOption, encrypt_version: encryptTypeProtocol }),
    parent_id: parentId,
    send_type: sendType,
    sender_device_id: deviceId,
    sender_id: accountId,
    source_id: sourceId,
    state: 1,
    status: SystemConstant.MESSAGE_STATUS.read,
    thread_id: threadId,
    call_status: callStatus,
    room_id: roomId,
  };

  if (saveMessage.send_type === SystemConstant.SEND_TYPE.leaveGroup && !removingId) {
    yield removeGroupInLocal(prefixKey, groupDetail);
  }

  yield saveMessageInDatabase(prefixKey, saveMessage);
  yield updateThread(prefixKey, saveMessage);

  const device = deviceList.find(s => s.id === deviceId);
  if (!device) return;

  const createdMessage = yield select(state => state.callingRedux.createdMessage);

  if (SystemConstant.ARR_CALLING_TYPES.includes(sendType) && !parentId) {
    yield put(
      CallingActions.callingSet({
        message: toCamel(saveMessage),
        createdMessage: {
          ...createdMessage,
          [groupDetail.id]: toCamel(saveMessage),
        },
      }),
    );
  }
  if (hasEncryption) {
    const updateMessage = { message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.encrypting };
    getInteractor(prefixKey).LocalMessageService.updateMessage(messageId, updateMessage);
  }
  let cipherObject = hasEncryption
    ? yield getInteractor(prefixKey).LocalCryptoService.encryptE2EEMessage(
        device.account_id,
        device.id,
        groupDetail.id,
        content,
        encryptTypeProtocol,
      )
    : content;

  const text = hasEncryption ? cipherObject?.cipherText : content;

  if (text) {
    if (hasEncryption) {
      const senderKeyEntity = toCamel(
        yield getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
          device.account_id,
          device.id,
          groupDetail.id,
          encryptTypeProtocol,
        ),
      );
      let prevIndexNumber = senderKeyEntity?.prev_index ?? -1;
      messageOption.index_send_number = cipherObject.indexSendNumber;
      messageOption.prev_send_number = prevIndexNumber;
    }

    const mesId = device.id === deviceId ? messageId : uuid();

    const newCurrentMessage = {
      messageId: mesId,
      sendToDeviceId: device.id,
      sendToAccountId: device.account_id,
      content: text,
      options: JSON.stringify({ ...messageOption, encrypt_version: encryptTypeProtocol }),
      status: SystemConstant.MESSAGE_STATUS.send,
      sendType: sendType,
      mentions: getMentions(mentionIdsArr),
      callStatus: callStatus,
      roomId: roomId,
      threadId: threadId,
      removingId: removingId,
    };
    let newDeviceList;
    if (hasEncryption) {
      newDeviceList = deviceList.filter(d => d.key_f === SystemConstant.DEVICE_KEY_STATE.correct).map(s => s.id);
    } else {
      newDeviceList = deviceList.map(s => s.id);
    }
    const newMessage = {
      isSendingKey: false,
      groupId: groupDetail.id,
      sourceId: sourceId,
      groupType: groupDetail.groupType,
      sendType: sendType,
      created: Date.now(),
      messages: [newCurrentMessage],
      deviceList: newDeviceList,
      branchId: branchId,
      parentId: parentId,
      threadId: threadId,
      roomId: roomId,
      removingId: removingId,
      options: JSON.stringify({ ...messageOption, encrypt_version: encryptTypeProtocol }),
      messageId: mesId,
      forward: forward,
    };

    try {
      if (hasEncryption) {
        const messageEncryptMetaData = yield getInteractor(
          prefixKey,
        ).LocalCryptoService.createMessageEncryptionMetaData(
          accountId,
          deviceId,
          content,
          sendType,
          groupDetail.id,
          messageOption.index_send_number,
          false,
          encryptTypeProtocol,
        );
        let sessionMessageKey = yield getInteractor(prefixKey).LocalCryptoService.getSenderMessageKey(
          accountId,
          deviceId,
          groupDetail.id,
          encryptTypeProtocol,
        );

        const messageEncrypt = {
          id: newMessage.messageId,
          source_id: sourceId,
          send_to_account_id: accountId,
          content: text,
          group_id: groupDetail.id,
          message_key: sessionMessageKey,
          session_data: messageEncryptMetaData.session_data,
          file_id: messageEncryptMetaData.file_id,
          key_exchange_algorithm: messageEncryptMetaData.key_exchange_algorithm,
          plain_text: messageEncryptMetaData.plain_text,
          send_to_device_id: deviceId,
          options: newMessage.options,
          created: Date.now(),
          modified: Date.now(),
          index_number: messageEncryptMetaData.index_number,
          waiting_number: messageEncryptMetaData.waiting_number,
          error_number: messageEncryptMetaData.error_number,
          success_number: messageEncryptMetaData.success_number,
          pre_index: messageOption.prev_send_number,
          session_type: SystemConstant.SESSION_TYPE.sender,
          send_type: newMessage.sendType,
          group_type: groupDetail.groupType,
          sender_id: accountId,
          sender_device_id: deviceId,
        };
        yield getInteractor(prefixKey).LocalMessageEncryptService.save([messageEncrypt]);
        // Cập nhật prev_index cho sender_key
        let updateValues = {
          prev_index: messageOption.index_send_number,
        };
        yield getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
          updateValues,
          device.account_id,
          device.id,
          groupDetail.id,
          encryptTypeProtocol,
        );
      }
      const updateMessage = {
        message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
        options: newMessage.options,
      };
      getInteractor(prefixKey).LocalMessageService.updateMessage(messageId, updateMessage);
    } catch (error) {
      console.error(error);
    }

    const apiCallTask = getApiCallTask(sendType);
    yield getInteractor(prefixKey).LocalApiCallService.save([
      {
        id: uuid(),
        task: apiCallTask,
        original_uid: sourceId,
        query: "",
        content: JSON.stringify({ data: newMessage }),
        original_content: JSON.stringify({ data: newMessage }),
        created: new Date().getTime(),
        retry: 0,
        branch_id: branchId,
        group_id: newMessage.groupId,
      },
    ]);
    yield put(SystemActions.executeQueue());
  }
}

function* sendE2EMessage(prefixKey, data) {
  try {
    const branchInfo = StorageUtil.getItem(KeyConstant.KEY_BRANCH_INFO, prefixKey) || {};
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
    const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
    const {
      groupDetail,
      parentId,
      content,
      sendType,
      branchId,
      threadId,
      mentionIdsArr,
      memberIdArray,
      callStatus,
      isReceiver,
      option,
      currentMessage,
      forward,
    } = data;
    const messageContent = convertJSONObject(content);
    const roomId = messageContent?.room_id || null;
    const messageOption = isJSONString(option || currentMessage?.options)
      ? convertString2JSON(option || currentMessage?.options, {})
      : option || currentMessage?.options || {};
    if (messageOption.encryption_f !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION)
      messageOption.encryption_f = SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION;

    messageOption.original_send_type = sendType;
    messageOption.target_decrypt_send_type = SystemConstant.SEND_TYPE.message;

    let messageId = uuid();
    let sourceId = uuid();
    let created = Date.now();
    if (currentMessage) {
      messageId = currentMessage.id;
      sourceId = currentMessage.source_id;
      if (currentMessage.created) created = currentMessage.created;
    }
    let deviceLocal = yield getInteractor(prefixKey).LocalDeviceService.get(deviceId);
    const encryptVersionLocal = yield getProtocol(prefixKey, deviceLocal);

    const saveMessage = {
      account_id: accountId,
      branch_id: branchInfo?.id,
      content: content,
      created: created,
      device_id: deviceId,
      group_id: groupDetail.id,
      id: messageId,
      mentions: getMentions(mentionIdsArr),
      modified: 0,
      options: JSON.stringify({ ...messageOption, encrypt_version: encryptVersionLocal }),
      parent_id: parentId,
      send_type: sendType,
      sender_device_id: deviceId,
      sender_id: accountId,
      source_id: sourceId,
      state: 1,
      status: SystemConstant.MESSAGE_STATUS.read,
      thread_id: threadId,
      call_status: callStatus,
    };

    if (saveMessage.send_type === SystemConstant.SEND_TYPE.leaveGroup) {
      yield removeGroupInLocal(prefixKey, groupDetail);
    }

    yield saveMessageInDatabase(prefixKey, saveMessage);
    yield updateThread(prefixKey, saveMessage);

    const deviceList = yield getInteractor(prefixKey).LocalDeviceService.find({
      account_id: memberIdArray,
      state: SystemConstant.STATE.active,
    });

    const updateMessage = { message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.encrypting };
    yield getInteractor(prefixKey).LocalMessageService.updateMessage(messageId, updateMessage);

    const values = yield Promise.all(
      deviceList.map(async device => {
        let jsonOption = {};
        const encryptVersion = await getProtocol(prefixKey, device);
        const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
        const encryption_f = isEncryptionType(device, sendType);
        let text;
        if (encryption_f !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION) {
          let cipherObject = await getInteractor(prefixKey).LocalCryptoService.encryptE2EMessage(
            device.account_id,
            device.id,
            groupDetail.id,
            content,
            encryptTypeProtocol,
          );
          text = cipherObject?.cipherText;
          if (!text) return null;
          const sessionEntity = toCamel(
            await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
              device.account_id,
              device.id,
              groupDetail.id,
              encryptTypeProtocol,
            ),
          );
          jsonOption.index_send_number = cipherObject?.indexSendNumber;
          jsonOption.prev_send_number = sessionEntity.prevIndex ?? -1;
        } else {
          text = content;
        }
        if (!text) return null;

        const mesId = device.id === deviceId ? messageId : uuid();

        let newCallStatus = callStatus;
        if (callStatus === SystemConstant.MESSAGE_CALL_STATUS.accept) {
          if (device.account_id === accountId && device.id !== deviceId) {
            newCallStatus = SystemConstant.MESSAGE_CALL_STATUS.inAnotherCall;
          } else if (
            (device.account_id === accountId && device.id === deviceId) ||
            isReceiver ||
            sendType === SystemConstant.SEND_TYPE.reconnect
          ) {
            newCallStatus = callStatus;
          } else {
            newCallStatus = SystemConstant.MESSAGE_CALL_STATUS.waiting;
          }
        }
        const message = {
          messageId: mesId,
          sendToDeviceId: device.id,
          sendToAccountId: device.account_id,
          content: text,
          options: JSON.stringify({ ...messageOption, ...jsonOption, encryption_f, encrypt_version: encryptVersion }),
          status: SystemConstant.MESSAGE_STATUS.send,
          sendType: sendType,
          mentions: getMentions(mentionIdsArr),
          callStatus: newCallStatus,
          roomId: roomId,
        };

        try {
          const messageEncryptMetaData = await getInteractor(
            prefixKey,
          ).LocalCryptoService.createMessageEncryptionMetaData(
            message.sendToAccountId,
            message.sendToDeviceId,
            content,
            sendType,
            groupDetail.id,
            jsonOption.index_send_number,
            true,
            encryptTypeProtocol,
          );
          let sessionMessageKey = "";
          if (encryption_f !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION) {
            sessionMessageKey = await getInteractor(prefixKey).LocalCryptoService.getSessionSenderMessageKey(
              accountId,
              deviceId,
              groupDetail.id,
              encryptTypeProtocol,
            );
            const messageEncrypt = {
              id: message.messageId,
              source_id: sourceId,
              send_to_account_id: message.sendToAccountId,
              content: message.content,
              group_id: groupDetail.id,
              message_key: sessionMessageKey,
              session_data: messageEncryptMetaData.session_data,
              file_id: messageEncryptMetaData.file_id,
              key_exchange_algorithm: messageEncryptMetaData.key_exchange_algorithm,
              plain_text: messageEncryptMetaData.plain_text,
              send_to_device_id: message.sendToDeviceId,
              options: message.options,
              created: Date.now(),
              modified: Date.now(),
              index_number: messageEncryptMetaData.index_number,
              waiting_number: messageEncryptMetaData.waiting_number,
              error_number: messageEncryptMetaData.error_number,
              success_number: messageEncryptMetaData.success_number,
              pre_index: messageEncryptMetaData.prev_number,
              session_type: SystemConstant.SESSION_TYPE.session,
              send_type: message.sendType,
              group_type: groupDetail.groupType,
              sender_id: accountId,
              sender_device_id: deviceId,
            };
            await getInteractor(prefixKey).LocalMessageEncryptService.save([messageEncrypt]);

            // Cập nhật prev_index cho session
            let updateValues = {
              prev_index: jsonOption.index_send_number,
            };
            await getInteractor(prefixKey).LocalCryptoService.updateSession(
              updateValues,
              device.account_id,
              device.id,
              groupDetail.id,
              encryptTypeProtocol,
            );
          }
          if (device.id === saveMessage.device_id) {
            const updateMessageMap = {
              message_encrypt_status: SystemConstant.MESSAGE_ENCRYPT_STATUS.done,
              options: message.options,
            };
            getInteractor(prefixKey).LocalMessageService.updateMessage(messageId, updateMessageMap);
          }
        } catch (error) {
          console.error(error);
        }

        return message;
      }),
    );
    const validMessages = values.filter(msg => msg !== null);

    // Skip if dont have any message
    if (validMessages.length === 0) throw Error("EMPTY_MESSAGE");
    const newMessage = {
      isSendingKey: false,
      groupId: groupDetail.id,
      sourceId: sourceId,
      groupType: groupDetail.groupType,
      sendType: sendType,
      created: Date.now(),
      messages: validMessages,
      branchId: branchId,
      parentId: parentId,
      threadId: threadId,
      roomId: roomId,
      messageId: messageId,
      forward: forward,
    };

    const apiCallTask = getApiCallTask(sendType);
    yield getInteractor(prefixKey).LocalApiCallService.save([
      {
        id: uuid(),
        task: apiCallTask,
        original_uid: sourceId,
        query: "",
        content: JSON.stringify({ data: newMessage }),
        original_content: JSON.stringify({ data: newMessage }),
        created: new Date().getTime(),
        retry: 0,
        branch_id: branchId,
        group_id: newMessage.groupId,
      },
    ]);
    yield put(SystemActions.executeQueue());

    const createdMessage = yield select(state => state.callingRedux.createdMessage);

    if (SystemConstant.ARR_CALLING_TYPES.includes(sendType) && !parentId) {
      yield put(
        CallingActions.callingSet({
          message: toCamel(saveMessage),
          createdMessage: {
            ...createdMessage,
            [groupDetail.id]: toCamel(saveMessage),
          },
        }),
      );
    }
  } catch (e) {
    console.trace(e); // eslint-disable-line no-console
  }
}

// Delete group in local db when send message leaveGroup
function* removeGroupInLocal(prefixKey, groupDetail) {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);

  const savingGroup = {
    state: SystemConstant.STATE.inactive,
    modified: Date.now(),
  };
  const isPersonalConversation = groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal;

  if (isPersonalConversation) {
    const options = groupDetail.options ? JSON.parse(groupDetail.options) : {};

    if (options.hidden && Array.isArray(options.hidden) && !options.hidden.includes(accountId)) {
      options.hidden.push(accountId);
    } else {
      options.hidden = [accountId];
    }
    savingGroup.options = JSON.stringify(options);
  } else {
    yield getInteractor(prefixKey).LocalAccountGroupService.removeMemberGroup(groupDetail.id, accountId);
  }

  yield getInteractor(prefixKey).LocalGroupService.update(savingGroup, { id: groupDetail.id });

  yield put(
    GroupInfoActions.groupInfoSuccess({
      deleteGroup: { groupId: groupDetail.id, modified: Date.now() },
    }),
  );

  const selectedGroupId = yield select(ConversationSelectors.getSelectedGroupId);
  if (selectedGroupId === groupDetail.id) {
    yield put(
      ConversationActions.setSelectGroupId({
        threadingId: null,
        selectedGroupId: null,
      }),
    );
  }
}

const getMentions = mentionIdsArr =>
  JSON.stringify(Array.isArray(mentionIdsArr) ? mentionIdsArr : convertString2JSON(mentionIdsArr, []));

const getApiCallTask = sendType => {
  let apiCallTask = AppConstant.API_CALL_TASK.message;
  switch (sendType) {
    case SystemConstant.SEND_TYPE.deleteMessageOnlyMe:
      apiCallTask = AppConstant.API_CALL_TASK.delayMessage;
      break;

    default:
      break;
  }

  return apiCallTask;
};

const saveMessageInDatabase = async (prefixKey, saveMessage) => {
  const localMessage = await getInteractor(prefixKey).LocalMessageService.findOne({ id: saveMessage.id });

  if (false === Boolean(localMessage && localMessage.id)) {
    if (saveMessage.send_type === SystemConstant.SEND_TYPE.deleteMessageOnlyMe) {
      const deleteMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
        source_id: saveMessage.parent_id,
      });

      if (deleteMessage?.state === SystemConstant.STATE.active) {
        store.dispatch(
          ConversationActions.conversationSet({
            deleteOnlyMeMsg: {
              message: toCamel(deleteMessage),
              isDelete: true,
            },
          }),
        );
      }
    }
    await getInteractor(prefixKey).LocalMessageService.save([saveMessage]);
  }
};
